import { Injectable } from '@angular/core';
import { HttpService } from '../store/http/http.service';
import { RequestType } from '../store/http/http.service';
import { catchError, map } from 'rxjs/operators';
import { Project } from './project.interface';
import { Observable, of } from 'rxjs';
import { IRIToId, IdToIRI } from '../util/iri';
import { User } from '../user/user.interface';
import { UserService } from '../user/user.service';
import {
  ResourceData,
  SimpleProjectDescriptor,
} from '../elements/resource/resource.types';
import { cloneDeep, pick } from 'lodash';
import { Store } from '@ngrx/store';
import { addSceneAfter } from './project.actions';
import * as uuidv1 from 'uuid/v1';
import {
  BgType,
  SimplifiedDescriptor,
} from '../elements/resource/types/shape.type';
import { CanvasService } from '../services/canvas/canvas.service';
import { ShapeService } from '../element-editor/shape/shape.service';
import { BackgroundService } from '../element-editor/shape/background.service';
import { selectCurrentProjectID } from '../element-editor/store/selector/editor.selector';
import { setDescriptorValue } from '../element-editor/store/editor.crud.actions';
import { EllipseBackgroundOptions } from '../element-editor/shape/shapes/background/ellipse-background';
import { FileRootShape } from '../element-editor/shape/shapes/general/root/file-root-shape';
import { AnimationService } from '../element-editor/animation/animation.service';

@Injectable()
export class ProjectService {
  saveName(project: Project, label: string): Observable<Project> {
    return this.http
      .patch({
        path: `project/label/${project.id}`,
        body: {
          label,
        },
      })
      .pipe(
        map(() => ({
          ...project,
          name: label,
        })),
      );
  }

  saveProjectDescriptor(project: Project, orgID?: string) {
    return this.http.requestCall({
      type: RequestType.PATCH,
      path: 'project/descriptor/' + project.id,
      headers: {
        'organisation-id': orgID,
      },
      body: {
        colorPalette: project.colorPalette,
        languages: project.languages,
        library: project.library,
      },
    });
  }

  deleteFile(fileID: string, projectID: string, orgID: string) {
    return this.http.requestCall({
      type: RequestType.DELETE,
      path: `project/${projectID}/file/${fileID}`,
      headers: {
        'organisation-id': orgID,
      },
    });
  }

  createProject(name: string, orgID: string) {
    return this.http
      .requestCall({
        type: RequestType.POST,
        path: 'organisation/project',
        headers: {
          'organisation-id': orgID,
        },
        body: {
          name,
        },
      })
      .pipe(map(project => this.mapResourceToProject(project)));
  }

  deleteProject(projectID: string, orgID) {
    return this.http
      .requestCall({
        type: RequestType.DELETE,
        path: 'project/' + projectID,
        headers: {
          'organisation-id': orgID,
        },
      })
      .pipe(map(() => projectID));
  }

  listProjects(): Observable<Project[]> {
    return this.http
      .requestCall({ path: 'organisation-crud/project', type: RequestType.GET })
      .pipe(
        map(projects =>
          projects.map(project => this.mapResourceToProject(project)),
        ),
      );
  }

  patchFile(file: ResourceData): Observable<ResourceData> {
    return this.http.requestCall({
      path: 'editor/file',
      type: RequestType.POST,
      body: file,
    });
  }

  loadProjectById(projectId: string): Observable<{
    project: Project;
    users: User[];
    files: Record<string, ResourceData>;
  }> {
    console.log('load-project by id....x');
    return this.http
      .requestCall({
        path: `project/${projectId}`,
        type: RequestType.GET,
        // headers: {
        //   'organisation-id': this.userService.getOrganisationId(),
        // },
      })
      .pipe(
        map(project => ({
          project: this.mapResourceToProject(project),
          users: project.relationships?.roles.map(role =>
            this.userService.mapResourceToUser(role.relationships.user),
          ),
          files: project.relationships.files.reduce((object, file) => {
            object[file.IRI] = file;
            return object;
          }, {}),
        })),
        catchError(error => {
          console.error('error', error);
          return of({
            project: null,
            users: [],
            files: {},
          });
        }),
      );
  }

  loadProjectWithFiles(
    projectId: string,
    orgID: string,
  ): Observable<ResourceData> {
    return this.http.requestCall({
      path: `project/files/${projectId}/new`,
      type: RequestType.GET,
      headers: {
        'organisation-id': orgID,
      },
    });
  }

  url =
    'https://cdn.prod.website-files.com/64c76d75d406d30124868bbc/6508374e3c3c6d169cbc52a8_Mainpage_%201441.jpg';
  descriptorToSave = {
    type: 'container',
    company: 'voovo_study',
    page: 'home',
    slides: {
      '1': {
        type: 'container',
        topology: 'column',
        margin: 100,
        elements: [
          {
            type: 'text',
            text: 'Hello World',
          },
          {
            type: 'image',
            url: this.url,
          },
        ],
      },
      '2': {
        type: 'container',
        topology: 'row',
        margin: 100,
        elements: [
          {
            type: 'image',
            url: this.url,
          },
          {
            type: 'text',
            text: 'Hello World 2',
          },
        ],
      },
    } as Record<string, SimplifiedDescriptor>,
  };

  saveResources() {
    console.log('project.service > saveResources');
    // -- // -- //
    // const resourceData = this.mapSimplfiedDescriptorToResourceData(
    //   this.descriptorToSave,
    // );
    // console.log('mapped-resourceData', resourceData);
    // const posts = this.flatResourceData(resourceData);

    const { posts } = this.simplifiedToResourcePatch(this.descriptorToSave);
    return this.http.requestCall({
      type: RequestType.PATCH,
      path: 'shape',
      body: {
        file: {},
        posts,
        patches: [],
        deletes: [],
      },
    });
    // .pipe(map(() => saveResourceSuccess()));
  }

  getNewIRI() {
    return `http://nowords.com#${uuidv1()}`;
  }

  simplifiedToResourcePatch(descriptor: {
    company: string;
    page: string;
    slides: Record<string, SimplifiedDescriptor>;
  }) {
    const posts = [];
    Object.entries(descriptor.slides).forEach(([key, value]) => {
      posts.push(...this.getContainerResourceData(value, { file: true }));
    });
    // -- // -- //
    return { posts };
  }

  getContainerResourceData(
    descriptor: SimplifiedDescriptor,
    { file, shapeIRI }: { file?: boolean; shapeIRI?: string },
  ): ResourceData[] {
    const data = [];
    const resourceIRI = this.getNewIRI();
    data.push({
      type: file ? 'nw:File' : 'nw:Shape',
      IRI: resourceIRI,
      literals: {
        descriptor: {
          type: file ? undefined : 'container-shape',
          topology: descriptor.topology,
          margin: descriptor.margin,
        },
      },
      relationships: {
        file: file ? undefined : resourceIRI,
        isIn: file ? IdToIRI(this.cs.currentProjectID) : undefined,
      },
    });

    descriptor.elements.forEach((element, index) => {
      if (element.type == 'container') {
        // -- // -- //
        data.push(
          ...this.getContainerResourceData(element, { shapeIRI: resourceIRI }),
        );
      } else {
        data.push({
          type: 'nw:Shape',
          IRI: this.getNewIRI(),
          literals: {
            descriptor: {
              type: element.type + '-shape',
              containerIndex: index,
              s3Id: element.url,
              textConfig: element.text
                ? { text: element.text, fontSize: 20 }
                : undefined,
            },
          },
          relationships: {
            file: resourceIRI,
          },
        });
      }
    });
    return data;
  }

  mapSimplfiedDescriptorToResourceData(
    descriptor: SimplifiedDescriptor,
  ): ResourceData {
    return {
      type: 'nw:Shape',
      IRI: this.getNewIRI(),
      literals: {
        descriptor: {
          type: descriptor.type + '-shape',
          // containerIndex: descriptor.containerIndex,
          ...pick(descriptor, ['margin', 'topology']),
          s3Id: descriptor.url,
          textConfig: descriptor.text
            ? { text: descriptor.text, fontSize: 20 }
            : undefined,
        } as any,
      },
      relationships: {
        shapes: descriptor.elements?.reduce(
          (acc, element, index) => {
            const resource = this.mapSimplfiedDescriptorToResourceData(element);
            resource.literals.descriptor.index = index;
            acc[resource.IRI] = resource;
            return acc;
          },
          {} as Record<string, ResourceData>,
        ),
      },
    };
  }

  flatResourceData(resourceData: ResourceData) {
    const data = [];
    data.push({
      ...resourceData,
      relationships: {
        shapes: resourceData.relationships?.shapes
          ? Object.keys(resourceData.relationships?.shapes).reduce(
              (acc, key) => ({
                ...acc,
                [key]: true,
              }),
              {},
            )
          : undefined,
      },
    });
    Object.entries(
      (resourceData.relationships?.shapes as Record<string, ResourceData>) ||
        {},
    ).forEach(([key, value]) => {
      data.push(...this.flatResourceData(value));
    });
    // -- // -- //
    return data;
  }

  _loadProjectWithFiles(
    projectId: string,
    orgID: string,
  ): Observable<ResourceData> {
    // -- //
    const url =
      'https://cdn.prod.website-files.com/64c76d75d406d30124868bbc/6508374e3c3c6d169cbc52a8_Mainpage_%201441.jpg';

    const fileIRI = IdToIRI('1');
    const shapeIRI = IdToIRI('s1');
    const { files, shapes } = {
      files: {
        [fileIRI]: {
          IRI: fileIRI,
          literals: {
            descriptor: {
              simplifiedDescriptor: {
                type: 'container',
                topology: 'column',
                margin: 100,
                elements: [
                  {
                    type: 'text',
                    text: 'Help you students',
                    align: 'center',
                  },
                  {
                    type: 'text',
                    text: 'with personalized\nrepetition',
                    align: 'center',
                  },
                  // {
                  //   type: 'image',
                  //   url,
                  // },
                  // {
                  //   type: 'container',
                  //   topology: 'row',
                  //   elements: [
                  //     {
                  //       type: 'image',
                  //       url,
                  //     },
                  //     {
                  //       type: 'image',
                  //       url,
                  //     },
                  //   ],
                  // },
                ],
              },
            } as any,
          },
          relationships: {
            shapes: {
              // -- // -- // 'nw:s1': true, // -- // -- //
            },
          },
        },
      },
      shapes: {},
    };

    const res = this.mapResource({
      scenes: [
        {
          id: '1',
          shapes: [
            {
              id: '1.1',
              type: 'text',
              text: 'Hello World xyz',
              // type: 'image',
              // imageID: '67dec85f45ae714bffc0fbae.png',
              // imageID:
              //   'https://static.wixstatic.com/media/792312_951b403464524f64a3065aafbf133068~mv2.jpg',
            },
          ],
        },
        {
          id: '2',
          shapes: [
            {
              id: '1.2',
              type: 'text',
              text: 'Hello World 2',
              // type: 'image',
              // imageID: '67dec85f45ae714bffc0fbae.png',
              // imageID:
              //   'https://static.wixstatic.com/media/792312_951b403464524f64a3065aafbf133068~mv2.jpg',
            },
          ],
        },
      ],
    });

    // console.log('res', res);
    // console.log({ files, shapes });

    return of({
      IRI: IdToIRI(projectId),
      literals: {
        label: 'project',
        descriptor: {},
      },
      relationships: {
        files: Object.values(files) as ResourceData[],
        shapes,
        organisation: {
          IRI: IdToIRI(orgID),
        },
      },
    }) as Observable<ResourceData>;
  }

  mapResource(descriptor: SimpleProjectDescriptor): {
    files: Record<string, ResourceData>;
    shapes: Record<string, ResourceData>;
  } {
    console.log('------------------');
    const files = {};
    const shapes = {};

    descriptor.scenes.map(({ id, shapes: _shapes }, index) => {
      const IRI = IdToIRI(id);
      files[IRI] = {
        IRI,
        literals: {
          sceneIndex: index,
          descriptor: {},
        },
        relationships: {
          shapes: _shapes.reduce(
            (obj, shape) => ({
              ...obj,
              [IdToIRI(shape.id)]: true,
            }),
            {},
          ),
        },
        // relationships: {},
      };

      _shapes.map(shape => {
        const IRI = IdToIRI(shape.id);
        shapes[IRI] = {
          IRI,
          literals: {
            descriptor: {
              type: shape.type == 'image' ? 'image-shape' : 'text-shape',
              s3Id: shape.imageID,
              position: {
                x: 0,
                y: 0,
              },
              autoScale: true,
              textConfig: shape.text
                ? {
                    text: shape.text,
                    fontSize: 20,
                  }
                : undefined,
            },
          },
          relationships: {
            file: IRI,
          },
        };
      });
    });

    return { files, shapes };
  }

  addFileToProject(projectID: string, label: string): Observable<ResourceData> {
    return this.http.requestCall({
      path: `project/files`,
      type: RequestType.POST,
      body: {
        projectID,
        label,
      },
    });
  }

  mapResourceToProject({ IRI, literals, relationships }: any): Project {
    const organisationIRI = relationships?.organisation?.IRI;
    return {
      id: IRIToId(IRI),
      name: literals.label,
      loaded: true,
      colorPalette: literals.descriptor?.colorPalette,
      languages: literals.descriptor?.languages,
      library: literals.descriptor?.library,
      organisationId: organisationIRI && IRIToId(organisationIRI),
      roles:
        relationships.roles?.reduce(
          (roles, role) => ({
            ...roles,
            [IRIToId(role.relationships.user.IRI)]: {
              id: IRIToId(role.IRI),
              type: role.literals.type,
            },
          }),
          {},
        ) || {},
      files:
        cloneDeep(relationships)
          .files?.sort(f1 => (f1.literals.label == 'main' ? -1 : 1))
          .filter(file => !file.relationships.childOf) || [],
    };
  }

  mapProjectToResource({
    id,
    name,
    organisationId,
    roles,
    languages,
    library,
    colorPalette,
  }: Project) {
    return {
      IRI: IdToIRI(id),
      literals: {
        label: name,
        descriptor: {
          languages,
          colorPalette,
          library,
        },
      },
      relationships: {
        organisation: {
          IRI: IdToIRI(organisationId),
        },
        roles: Object.entries(roles || []).map(([userId, role]) => ({
          IRI: IdToIRI(role.id),
          literals: {
            type: role.type,
          },
          relationships: {
            user: {
              IRI: IdToIRI(userId),
            },
          },
        })),
      },
    };
  }

  currentProjectID: string;

  constructor(
    private readonly http: HttpService,
    private readonly userService: UserService,
    private readonly store: Store,
    private readonly cs: CanvasService,
    private readonly shapeService: ShapeService,
    private readonly backgroundService: BackgroundService,
    private readonly animationService: AnimationService,
  ) {
    // console.log('animationService', this.animationService);
    this.store.select(selectCurrentProjectID).subscribe(ID => {
      this.currentProjectID = ID;
      console.log('currentProjectID', this.currentProjectID);
    });
  }

  currentTimeline: gsap.core.Timeline;

  regenareteSceneAnimation() {
    this.currentTimeline?.kill();

    this.cs.currentRootShape.regenerateAnimation();
    this.currentTimeline = this.cs.currentRootShape.gsapAnimate(
      gsap.timeline(),
    );

    // -- //
    this.currentTimeline.eventCallback('onComplete', () => {
      this.cs.currentRootShape.revertSceneAnimation();
    });
  }
  regenerateBackground() {
    // console.log('regenerateBackground -------------------------'); // -- // -- //
    // const bg = this.backgroundService.regenerateBackground(); // -- //
    // const newB = {
    //   type: 'ellipse' as BgType,
    //   options: bg as EllipseBackgroundOptions,
    // };
    // console.log(
    //   '--------- setBgInTime ---------',
    //   (this.shapeService.cs.currentRootShape as FileRootShape)
    //     .animationStartTime,
    // );
    // this.backgroundService.setInTime(
    //   (this.shapeService.cs.currentRootShape as FileRootShape)
    //     .animationStartTime,
    //   newB,
    // );
    // this.backgroundService.initCB(newB); // -- //
    // (this.shapeService.cs.currentRootShape as FileRootShape).setBackground();
    // -- // -- //
    // -- // -- //
    // -- // -- //
    // setTimeout(() => {
    //   this.shapeService.updateBackground();
    // }, 10);
    // this.store.dispatch(
    //   setDescriptorValue({
    //     key: 'background',
    //     value: newB,
    //     IRI: IdToIRI(this.currentProjectID),
    //     direct: true,
    //   }),
    // );
  }
  createScene(params: {
    IRI: string;
    sceneIndex: number;
    parentFileID?: string;
    projectID: string;
    orgID: string;
    label?: string;
  }) {
    const { IRI, sceneIndex, parentFileID, projectID, orgID, label } = params;
    console.log('createScene > requestCall', {
      IRI,
      sceneIndex,
      parentFileID,
      projectID,
      orgID,
      label,
    });
    return this.http
      .requestCall({
        type: RequestType.POST,
        path: `project/${projectID}/scene`,
        headers: {
          'organisation-id': orgID,
        },
        body: {
          IRI,
          sceneIndex,
          parentFileID,
          label,
        },
      })
      .pipe(map(() => IRI));
  }
  addSceneAfter(params: { parentFileID?: string; label?: string } = {}) {
    const { parentFileID, label } = params;
    return this.store.dispatch(
      addSceneAfter({
        IRI: `http://nowords.com#${uuidv1()}`,
        parentFileID,
        label,
      }),
    );
  }

  deleteScene(sceneIRI: string, projectID: string, orgID: string) {
    return this.http
      .requestCall({
        type: RequestType.DELETE,
        path: `project/${projectID}/scene/${sceneIRI}`,
        headers: {
          'organisation-id': orgID,
        },
      })
      .pipe(map(() => sceneIRI));
  }
}
